import { useCallback, useMemo } from "react";
import useSWR, { type KeyedMutator } from "swr";

import {
  clearCache as clearMemoryCache,
  getCacheAge,
  getFromCache,
  isCacheStale,
  setToCache,
} from "../utils/memoryCache";

const DEFAULT_MAX_AGE = 5 * 60 * 1000; // 5 minutes - deduplication interval
const DEFAULT_MAX_STALE_AGE = 60 * 60 * 1000; // 1 hour - max age before cached data is discarded

interface UseFetchOptions {
  deps?: unknown[];
  auto?: boolean | null;
  cacheKey?: string | null;
  maxAge?: number;
  maxStaleAge?: number;
}

const defaultOptions: UseFetchOptions = {
  deps: [],
  auto: true,
  cacheKey: null,
  maxAge: DEFAULT_MAX_AGE,
  maxStaleAge: DEFAULT_MAX_STALE_AGE,
};

interface UseFetchReturn<T> {
  /** Primary property name for the fetched data */
  data: T | undefined;
  /** Backward compatibility with useCachedRequest/useRequest */
  state: T | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  /** Expose mutate for advanced usage (optimistic updates, etc.) */
  mutate: KeyedMutator<T>;
}

/**
 * Unified data fetching hook with SWR-based caching and deduplication.
 *
 * Features:
 * - Persists cache to localStorage (survives page refresh)
 * - Returns cached data immediately while fetching fresh data (stale-while-revalidate)
 * - Discards cached data older than maxStaleAge (default: 1 hour) to avoid showing very old data
 * - Request deduplication: multiple components with same cacheKey = 1 fetch
 * - All subscribing components update simultaneously when fresh data arrives
 * - **Type-safe**: Infers return type from the request function
 *
 * @example
 * // Basic usage (no caching) - type is inferred from API endpoint
 * const { data, isLoading } = useFetch(() => api.getUsers());
 * // data is typed as User[] | undefined
 *
 * @example
 * // With caching and deduplication
 * const { data, isLoading, isRefreshing } = useFetch(
 *   () => api.getUser(userId),
 *   { cacheKey: `user_${userId}`, deps: [userId] }
 * );
 *
 * @example
 * // Extract .data from ApiResponse inside the callback
 * const { state: users } = useFetch(() =>
 *   usersEndpoints.getUsers().then(({ data }) => data)
 * );
 */
function useFetch<T>(request: () => Promise<T>, options: UseFetchOptions = defaultOptions): UseFetchReturn<T> {
  const { deps, auto, cacheKey, maxAge, maxStaleAge } = { ...defaultOptions, ...options };

  // Serialize deps for stable dependency comparison
  const serializedDeps = useMemo(() => JSON.stringify(deps), [deps]);

  // Create a stable fetcher function
  // serializedDeps is used instead of deps to track deep changes
  const fetcher = useCallback(async (): Promise<T> => {
    const result = await request();
    // Also update legacy memoryCache for components that read directly from it
    if (cacheKey) {
      setToCache(cacheKey, result);
    }
    return result;
  }, [request, cacheKey]);

  // Build SWR key - null key disables fetching
  // Use a unique key per hook instance when cacheKey is not provided (no dedup)
  const instanceKey = useMemo(() => `_uncached_${Math.random()}`, []);
  const swrKey = useMemo(() => {
    if (!auto) return null;
    // When no cacheKey, use instance key (each component fetches independently)
    const baseKey = cacheKey || instanceKey;
    // Include serializedDeps in key to trigger refetch when deps change
    return serializedDeps !== "[]" ? [baseKey, serializedDeps] : baseKey;
  }, [auto, cacheKey, instanceKey, serializedDeps]);

  // Get initial data from localStorage cache (returns null if data is too old)
  const fallbackData = useMemo(() => {
    if (!cacheKey) return undefined;
    const cached = getFromCache<T>(cacheKey, maxStaleAge);
    return cached?.data;
  }, [cacheKey, maxStaleAge]);

  const { data, error, isLoading, isValidating, mutate } = useSWR<T>(swrKey, fetcher, {
    fallbackData,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: maxAge,
    errorRetryCount: 2,
    keepPreviousData: true,
  });

  // Manual refresh function (maintains backward compatibility with both old hooks)
  const refresh = useCallback(async () => {
    // mutate() triggers revalidation - SWR handles deduplication
    await mutate();
  }, [mutate]);

  // SWR's isLoading doesn't consider fallbackData as "loaded data"
  // We want to show cached data immediately, so only show loading if no data at all
  const hasData = data !== undefined;
  const effectiveIsLoading = !hasData && isLoading;
  const effectiveIsRefreshing = hasData && isValidating;

  return {
    // Primary property name
    data,
    // Backward compatibility with useCachedRequest/useRequest
    state: data,
    isLoading: effectiveIsLoading,
    isRefreshing: effectiveIsRefreshing,
    error: error || null,
    refresh,
    // Expose mutate for advanced usage (optimistic updates, etc.)
    mutate,
  };
}

// Export cache utilities for manual cache management
export const clearCache = clearMemoryCache;
export { getCacheAge, isCacheStale };

export default useFetch;
